import React, { useState, useEffect, useCallback, startTransition } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DesktopView from "../layout/DesktopView";
import DocumentationCard from "./DocumentationCard";
import { systemFlowDiagram } from "./diagrams";
import HelpModal from "../help/HelpModal";
import { markdownDocuments } from "./documents";
import FlowDiagram from "./FlowDiagram";

export const documents = [
  ...markdownDocuments,
  {
    id: systemFlowDiagram.id,
    title: systemFlowDiagram.title,
    description: systemFlowDiagram.description,
    component: FlowDiagram,
    props: {
      nodes: systemFlowDiagram.nodes,
      edges: systemFlowDiagram.edges,
    },
  },
];

const DocumentationDesktop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [goBack, setGoBack] = useState(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const fetchDocument = useCallback((id) => {
    return documents.find((doc) => doc.id === id);
  }, []);

  useEffect(() => {
    if (searchParams.has("id") && !selectedDoc) {
      const docId = searchParams.get("id");
      const doc = fetchDocument(docId);
      if (doc) {
        setSelectedDoc(doc);
        if (doc.component === "HelpModal") {
          setIsHelpModalOpen(true);
        }
        if (searchParams.has("goback")) {
          setGoBack(searchParams.get("goback"));
          setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("goback");
            return newParams;
          });
        }
      }
    }
  }, [searchParams, selectedDoc, fetchDocument, setSearchParams]);

  const handleSearch = useCallback((term) => {
    // This function is left empty as search is handled internally by DesktopView
  }, []);

  const handleCloseDocViewer = useCallback(() => {
    // Must commit with React Router v7's transitioned URL update, or the id effect reopens the viewer.
    startTransition(() => setSelectedDoc(null));
    setIsHelpModalOpen(false);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("id");
      return newParams;
    });
    if (goBack) {
      const goto = goBack;
      setGoBack(null);
      navigate(goto + "?reload=true");
    }
  }, [setSearchParams, goBack, navigate]);

  const handleDocClick = useCallback(
    (doc) => {
      setSelectedDoc(doc);
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("id", doc.id);
        return newParams;
      });
      if (doc.component === "HelpModal") {
        setIsHelpModalOpen(true);
      }
    },
    [setSearchParams]
  );

  const DocumentEditor = useCallback(
    ({ item, onClose }) => {
      if (item.component === "HelpModal") {
        return (
          <HelpModal
            fileName={item.props.doc}
            isOpen={isHelpModalOpen}
            onClose={onClose}
          />
        );
      } else {
        const Component = item.component;
        return (
          <Component
            title={item.title}
            description={item.description}
            {...item.props}
            onClose={onClose}
          />
        );
      }
    },
    [isHelpModalOpen]
  );

  return (
    <DesktopView
      items={documents}
      selectedItem={selectedDoc}
      onSearch={handleSearch}
      searchPlaceholder="Search documentation ..."
      contextMenuItems={[]}
      onItemClick={handleDocClick}
      onSave={() => {}}
      onDelete={() => {}}
      onReset={() => {}}
      onClose={handleCloseDocViewer}
      Card={({ item, onClick }) => (
        <DocumentationCard
          title={item.title}
          description={item.description}
          onClick={() => onClick(item)}
        />
      )}
      Editor={DocumentEditor}
      addNewItem={() => {}}
      eventName="documentationCreated"
      hideOnSelect={!isHelpModalOpen}
    />
  );
};

export default DocumentationDesktop;
